import { useMe } from "@/hooks/queries/auth/useMe";
import { useAuthStore } from "@/stores/useAuthStore";
import type { Capability } from "@/lib/capabilities";

/**
 * Cosmetic capability gate for frontend affordances (RBAC FE WS-A).
 *
 * IMPORTANT: this is UX hygiene only, NOT a security boundary. The backend
 * enforces every authorization decision (capability decorators, seat-caps,
 * HM scope isolation). Hidden affordances merely reduce dead-click 403s; an
 * optimistically-shown action must still handle a backend 403/404.
 *
 * Resolution order (mirrors backend `has_capability`):
 *   1. SA short-circuit — `useAuthStore.isSuperAdmin === true` ⇒ true for
 *      every capability, unconditionally. Read from the JWT `is_sa` claim
 *      already in the store, so SA gating works even before `/auth/me`
 *      resolves and even when the backend returns `capabilities: []` for SA.
 *   2. Fail-closed — while `useMe` is loading OR `capabilities` is absent,
 *      return false. Gated UI stays hidden-until-known (no flash-of-
 *      affordance). `undefined` is treated as "no capability yet."
 *   3. Membership — `me.capabilities.includes(cap)`.
 *
 * NEVER re-derive from `role`; owner→admin is resolved server-side
 * (`ROLE_CAPABILITIES[OWNER]` == admin capset), so the FE needs no special
 * case. `/auth/me` is the single capability source — no fork from the backend
 * catalog.
 */
export function useCapability(cap: Capability): boolean {
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const { data: me } = useMe();

  // 1. SA short-circuit — independent of /auth/me having resolved.
  if (isSuperAdmin) {
    return true;
  }

  // 2. Fail-closed while loading or when capabilities is absent.
  const capabilities = me?.capabilities;
  if (capabilities === undefined) {
    return false;
  }

  // 3. Capability membership.
  return capabilities.includes(cap);
}
